import { useEffect, useRef } from 'react';
import { Form, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem, Button } from '@/shared/components/ui/atoms';
import { useResendTimer } from '@/features/auth/hooks';
import type { MFAFormData, MFAFormProps } from './types';
import './MFAForm.css';

/**
 * MFAForm Organism
 *
 * A reusable MFA verification form component with 6-digit code input.
 * Uses React Hook Form for validation and form state management.
 * Follows the same structure and patterns as LoginForm.
 * Renders an i18n-translated "Code" label above the MFA input field.
 *
 * @param {MFAFormProps} props - MFAForm component props
 * @param {(data: MFAFormData) => Promise<void>} props.onSubmit - Called with form data on valid submission
 * @param {() => Promise<void>} [props.onResendCode] - Called when user requests code resend
 * @param {() => void} [props.onBackToLogin] - Called when user clicks back to login
 * @param {() => void} [props.onClearError] - Called when user edits input after an error
 * @param {boolean} [props.loading=false] - Disables form controls while loading
 * @param {string | null} [props.error=null] - Error message to display in alert
 * @returns {React.ReactElement} A complete MFA form with validation, timer, and labeled input
 * @example
 * <MFAForm
 *   onSubmit={handleSubmit}
 *   onResendCode={handleResend}
 *   onBackToLogin={() => navigate('/login')}
 *   loading={false}
 *   error={null}
 * />
 */
const MFAForm: React.FC<MFAFormProps> = ({
  onSubmit,
  onResendCode,
  onBackToLogin,
  onClearError,
  loading = false,
  error = null,
}) => {
  const { t } = useLanguage();

  const mfaSchema = z.object({
    code: z
      .string()
      .length(6, t('Code must be 6 digits'))
      .regex(/^[0-9a-zA-Z]{6}$/, t('Invalid code format')),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { isSubmitting },
  } = useForm<MFAFormData>({
    resolver: zodResolver(mfaSchema),
    defaultValues: {
      code: '',
    },
  });

  const { timeRemaining, canResend, startTimer } = useResendTimer({
    initialTime: 30,
  });

  // Start timer on mount (simulating code sent)
  useEffect(() => {
    startTimer();
  }, [startTimer]);

  const codeValue = watch('code');

  // Track previous code value to detect user edits (typing or deleting)
  const prevCodeRef = useRef(codeValue);
  useEffect(() => {
    if (error && codeValue !== prevCodeRef.current) {
      onClearError?.();
      clearErrors('code');
    }
    prevCodeRef.current = codeValue;
  }, [codeValue, error, onClearError, clearErrors]);

  const handleFormSubmit = async (data: MFAFormData) => {
    await onSubmit(data);
  };

  const handleResend = async () => {
    if (!canResend || !onResendCode) return;

    try {
      await onResendCode();
      setValue('code', '');
      onClearError?.();
      clearErrors('code');
      startTimer();
    } catch {
      // Error will be handled by parent
    }
  };

  const isSubmitDisabled = codeValue.length !== 6 || loading || isSubmitting;

  return (
    <div className="mfa-form-wrapper">
      <div className="mfa-form-header">
        <div className="mfa-form-header-content">
          <p className="mfa-form-header-small">{t('Platform Name')}</p>
          <h1 className="mfa-form-header-title">{t('Email validation')}</h1>
        </div>

        <div className="mfa-form-header-description">
          <p>
            {t(
              'We sent a code to your email, fill the field below with the received code:'
            )}
          </p>
        </div>
      </div>

      {error && (
        <Alert
          message={
            <span>
              <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                {t('Attention!')}
              </span>{' '}
              {error}
            </span>
          }
          type="error"
          showIcon
          closable
        />
      )}

      <Form
        layout="vertical"
        onFinish={handleSubmit(handleFormSubmit)}
        noValidate
        className="mfa-form"
      >
        <ControlledFormItem<MFAFormData>
          name="code"
          control={control}
          errors={{} as Record<string, { message?: string }>}
          label={t('Code')}
          inputType="mfa"
          inputProps={{
            length: 6,
            error: !!error,
            disabled: loading || isSubmitting,
          }}
        />

        <div className="mfa-form-resend">
          <span className="mfa-form-resend-text">
            {t("Didn't receive the code?")}
          </span>
          <Button
            variant="link"
            fullWidth={false}
            onClick={handleResend}
            disabled={!canResend || loading}
          >
            {canResend
              ? t('Resend')
              : t('Resend ({{seconds}}s)', {
                  seconds: timeRemaining,
                })}
          </Button>
        </div>

        <Form.Item className="mfa-form-submit-item">
          <Button
            htmlType="submit"
            variant="primary"
            size="lg"
            maxWidth={326}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? t('Verifying...') : t('Verify')}
          </Button>
        </Form.Item>

        {onBackToLogin && (
          <Form.Item noStyle>
            <Button
              variant="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBackToLogin}
              disabled={loading || isSubmitting}
            >
              {t('Back to Login')}
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default MFAForm;
